import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { QRCodeSVG } from "qrcode.react";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import TorikizokuLogo from "../../assets/images/TorikizokuLogo.png";
import { decodeCart, encodeCart } from "../../common/helpers/cartSharing";
import PopupDialog from "../../components/elements/PopupDialog/PopupDialog";
import {
	useCartActions,
	useCartContext,
} from "../../contexts/CartServiceContext/CartServiceContext";
import { useCatalogueContext } from "../../contexts/CatalogueContext/CatalogueContext";
import { PATH } from "../../router/path";
import { addDatadogAction } from "../../services/datadog";
import "./ShareCartPage.scss";

type Mode = "qr" | "scan";

const ShareCartPage: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { state: cart } = useCartContext((state) => state.cart);
	const { state: products } = useCatalogueContext((state) => state.products);
	const cartActions = useCartActions();

	const [mode, setMode] = useState<Mode>("qr");
	const [scanError, setScanError] = useState<string | null>(null);
	const [pendingCart, setPendingCart] = useState<ReturnType<typeof decodeCart>>(null);
	const [showConfirm, setShowConfirm] = useState(false);

	const scannerRef = useRef<Html5Qrcode | null>(null);
	const scanningRef = useRef(false);
	const scanStartRef = useRef(0);
	const invalidLoggedRef = useRef(false);
	const SCANNER_ID = "share-cart-scanner";

	const encoded = cart && cart.length > 0 ? encodeCart(cart) : null;

	const stopScanner = useCallback(async () => {
		if (!scannerRef.current) return;

		try {
			if (scanningRef.current) {
				await scannerRef.current.stop();
			}
		} catch {
			// already stopped
		}

		scannerRef.current = null;
		scanningRef.current = false;
	}, []);

	const startScanner = useCallback(async () => {
		if (scanningRef.current) return;
		setScanError(null);

		try {
			// Only decode QR (skip all other barcode formats) and use the native
			// BarcodeDetector when the browser supports it — both speed up reads.
			const scanner = new Html5Qrcode(SCANNER_ID, {
				formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
				useBarCodeDetectorIfSupported: true,
			});

			scannerRef.current = scanner;
			scanningRef.current = true;
			scanStartRef.current = performance.now();
			invalidLoggedRef.current = false;

			await scanner.start(
				{ facingMode: "environment" },
				{ fps: 15, qrbox: { width: 250, height: 250 } },
				(text) => {
					const decoded = decodeCart(text, products ?? []);

					if (!decoded) {
						setScanError(t("shareCart.errorInvalid"));
						// Guard so a stationary invalid QR is not logged every frame.
						if (!invalidLoggedRef.current) {
							invalidLoggedRef.current = true;
							addDatadogAction("share_cart.scan_invalid");
						}

						return;
					}

					addDatadogAction("share_cart.scan_success", {
						duration_ms: Math.round(performance.now() - scanStartRef.current),
					});
					stopScanner();
					setPendingCart(decoded);
					setShowConfirm(true);
				},
				undefined
			);

			addDatadogAction("share_cart.scan_started");
		} catch {
			setScanError(t("shareCart.errorInvalid"));
			scanningRef.current = false;
			addDatadogAction("share_cart.scan_error");
		}
	}, [products, t, stopScanner]);

	useEffect(() => {
		if (mode === "scan") {
			startScanner();
		} else {
			stopScanner();
		}

		return () => {
			stopScanner();
		};
	}, [mode, startScanner, stopScanner]);

	const handleConfirmLoad = useCallback(() => {
		if (!pendingCart) return;
		cartActions.LOAD_SHARED_CART(pendingCart);
		setShowConfirm(false);
		navigate(PATH.CART);
	}, [pendingCart, cartActions, navigate]);

	const handleCancelLoad = useCallback(() => {
		setShowConfirm(false);
		setPendingCart(null);
		setMode("scan");
		startScanner();
	}, [startScanner]);

	const confirmButtons = [
		{ text: t("shareCart.confirmOk"), onClick: handleConfirmLoad },
		{ text: t("shareCart.confirmCancel"), onClick: handleCancelLoad },
	];

	return (
		<div className="ShareCartPage">
			<div className="ShareCartTabs">
				<button
					className={`ShareCartTab ${mode === "qr" ? "ShareCartTab--active" : ""}`}
					onClick={() => setMode("qr")}
				>
					{t("shareCart.showQrBtn")}
				</button>
				<button
					className={`ShareCartTab ${mode === "scan" ? "ShareCartTab--active" : ""}`}
					onClick={() => setMode("scan")}
				>
					{t("shareCart.scanBtn")}
				</button>
			</div>

			{mode === "qr" && (
				<div className="ShareCartQr">
					{encoded ? (
						<>
							<p className="ShareCartQrHeading">{t("shareCart.qrHeading")}</p>
							<div className="ShareCartQrCode">
								<QRCodeSVG
									value={encoded}
									size={240}
									// "M" (15% ECC) instead of "H" (30%) lowers the QR module
									// count for a faster, easier scan, while still leaving enough
									// error correction to recover the excavated centre logo.
									level="M"
									marginSize={2}
									imageSettings={{
										src: TorikizokuLogo,
										width: 56,
										height: 28,
										excavate: true,
									}}
								/>
							</div>
							<p className="ShareCartQrHint">{t("shareCart.qrHint")}</p>
						</>
					) : (
						<p className="ShareCartEmpty">{t("shareCart.emptyCart")}</p>
					)}
				</div>
			)}

			{mode === "scan" && (
				<div className="ShareCartScan">
					<p className="ShareCartScanHint">{t("shareCart.scanHint")}</p>
					<div id={SCANNER_ID} className="ShareCartVideoWrapper" />
					{scanError && <p className="ShareCartError">{scanError}</p>}
				</div>
			)}

			<PopupDialog
				data-testid="share-cart-confirm"
				open={showConfirm}
				title={t("shareCart.confirmTitle")}
				content={<span>{t("shareCart.confirmMsg")}</span>}
				buttons={confirmButtons}
				onClose={handleCancelLoad}
				className="ShareCartConfirmDialog"
			/>
		</div>
	);
};

export default ShareCartPage;
